// @avantia common-code
import {
  callRestApi,
  HpRequestBodyOptions,
  HpRequestOptions,
  HpValidateStatusFunction,
  HpWebRequestError,
  intervalToMs,
  setupRestApi,
  voidFunction
} from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logInfo, logWarning } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { HpCustomFailureHandlerResponse } from '../customExtensionRegistryTypes';
import { applyChanges } from '../reducers/reducerLibrary';
import { getAnswersIdCookie, getProductIdCookie } from '../tools/cookieUtilities';
import { postScriptError } from '../tools/postScriptError';
import { HpDispatchFunction, HpEngineActionTypes, HpFetchErrorPayload } from '../types';
import { HpFetchErrorOcurredAction, HpLoadingPageTypes } from '../types/ajaxCallActionTypes';

let productKey: string, answersKey: string;
registerConfigurationAction((config) => {
  ({ productKey, answersKey } = config);
});

/**
 * Props for the fetchResolver function.
 * Supports both URL-based requests (using HpRequestOptions/HpRequestBodyOptions) and pre-built promises.
 *
 * @template BodyModelT - The type of the request body (for POST/PUT requests).
 * @template ResponseModelT - The type of the expected response payload.
 */
export type HpFetchResolverProps<BodyModelT, ResponseModelT> = HpFetchResolverOptionProps<
  BodyModelT,
  ResponseModelT,
  HpRequestOptions | HpRequestBodyOptions<BodyModelT> | Promise<ResponseModelT>
>;

/**
 * Props for fetchResolverPromise when working with a pre-built promise.
 * Requires a description for error logging purposes.
 *
 * @template BodyModelT - The type of the request body.
 * @template ResponseModelT - The type of the expected response payload.
 */
export interface HpFetchResolverPromiseProps<BodyModelT, ResponseModelT>
  extends HpFetchResolverOptionProps<BodyModelT, ResponseModelT, Promise<ResponseModelT>> {
  /** Human-readable description of the request for logging and error messages. */
  description: string;
}

/** Function called when a request fails, allowing custom failure handling. */
type HpFailedActionFunction = (reason: unknown) => HpCustomFailureHandlerResponse;

/** Custom error handler function. Return 'resolve' to resolve the promise instead of rejecting. */
type HpCustomErrorHandlerFunction = (reason: unknown) => unknown;

/**
 * Base props interface for fetch resolver operations.
 * Contains all configurable options for handling async requests and their responses.
 */
interface HpFetchResolverOptionProps<
  BodyModelT,
  ResponseModelT,
  OptionsT = HpRequestOptions | HpRequestBodyOptions<BodyModelT>
> {
  /** The Redux action type to dispatch on success (alternative to successAction). */
  action?: HpEngineActionTypes;
  /** The Redux dispatch function. */
  dispatch: HpDispatchFunction<ResponseModelT>;
  /** Custom handler for failed requests. */
  failedAction?: HpFailedActionFunction;
  /** Custom error handler that can intercept errors before default handling. */
  customErrorHandler?: HpCustomErrorHandlerFunction;
  /** Type of loading indicator to show during the request. */
  loadingPageType?: HpLoadingPageTypes;
  /** Request options (HpRequestOptions/HpRequestBodyOptions) or a pre-built promise. */
  options: OptionsT;
  /** Whether to increment the error count on failure. Defaults to true. */
  shouldIncrementErrorCount?: boolean;
  /** Whether to show the error page on failure. */
  showErrorPage?: boolean;
  /** Custom success handler function (alternative to action). */
  successAction?: (payload: ResponseModelT) => void;
  /** Custom status code validation function. */
  validateStatus?: HpValidateStatusFunction;
}

const validateStatusAxios: HpValidateStatusFunction = (code) =>
  (code >= 200 && code < 300) || (code >= 400 && code < 500);

const validateStatus: HpValidateStatusFunction = (code) => code >= 200 && code < 300;

setupRestApi({
  config: { validateStatus: validateStatusAxios },
  validateStatus: validateStatus,
  logFailure: (reason) => {
    return logWarning(reason, HpClientDebugFlags.None);
  },
  logSuccess: (result) => {
    const { url, description, statusCode, timer } = result || {};
    return logInfo(
      { url, description, statusCode, responseTime: timer.elapsedTimeAsString() },
      HpClientDebugFlags.AjaxRequests
    );
  },
  timeout: intervalToMs('30s')
});

/**
 * Main entry point for making async requests with Redux integration.
 * Handles both URL-based requests and pre-built promises, managing loading states,
 * success/failure actions, and error handling automatically.
 *
 * @template BodyModelT - The type of the request body (for POST/PUT requests).
 * @template ResponseModelT - The type of the expected response payload.
 * @param props - Configuration options for the request.
 * @returns A promise that resolves when the request completes (success or failure actions have been dispatched).
 */
export function fetchResolver<BodyModelT, ResponseModelT>(
  props: HpFetchResolverProps<BodyModelT, ResponseModelT>
): Promise<void> {
  const { options, validateStatus } = props;
  const fetchOptions = options as HpRequestOptions;
  const { description, url } = fetchOptions;
  let promise: Promise<void>;
  if (url) {
    promise = fetchResolverPromise<BodyModelT, ResponseModelT>({
      ...props,
      description,
      options: fetchResolverBasic<BodyModelT, ResponseModelT>({ ...fetchOptions, validateStatus })
    });
  } else {
    promise = fetchResolverPromise(props as HpFetchResolverPromiseProps<BodyModelT, ResponseModelT>);
  }

  // Use 'voidFunction' so code quality checks don't complain.
  // This promise has already had success/failure actions performed and this just prevents
  // runtime complaints about unhandled promises.
  promise.then(voidFunction).catch(voidFunction);

  return promise;
}

/**
 * Resolves a pre-built promise with Redux integration.
 * Manages AJAX call lifecycle (started/completed actions), dispatches success or failure actions,
 * and handles error scenarios with appropriate user feedback.
 *
 * @template BodyModelT - The type of the request body.
 * @template ResponseModelT - The type of the expected response payload.
 * @param props - Configuration options including the promise to resolve.
 * @returns A promise that resolves when the operation completes.
 */
export function fetchResolverPromise<BodyModelT, ResponseModelT>({
  action,
  customErrorHandler,
  description,
  dispatch,
  failedAction,
  loadingPageType,
  options: promise,
  shouldIncrementErrorCount = true,
  showErrorPage,
  successAction
}: HpFetchResolverPromiseProps<BodyModelT, ResponseModelT>): Promise<void> {
  const requestSilently = loadingPageType === 'none';
  const ajaxCallId = new Date().getTime().toString();
  if (!requestSilently) {
    dispatch({ type: 'AJAX_CALL_STARTED', payload: { ajaxCallId, loadingPageType } as any });
  }

  return new Promise<void>((resolve, reject) => {
    const failedFunc: HpFailedActionFunction = failedAction
      ? (reason) => {
          const rsp = failedAction(reason);
          if (!rsp || rsp.rejectPromise !== false) {
            reject(reason);
          } else {
            resolve();
          }

          return rsp;
        }
      : reject;

    promise
      .then((payload) => {
        if (!requestSilently) {
          dispatch({ type: 'AJAX_CALL_COMPLETED', payload: { ajaxCallId } as any });
        }

        if (successAction && lodash.isFunction(successAction)) {
          successAction(payload);
        } else if (action) {
          dispatch({ type: action, payload });
        } else {
          return reject("Either 'action' or 'successAction' must be provided.");
        }

        resolve();
      })
      .catch((reason) => {
        if (customErrorHandler) {
          const action = customErrorHandler(reason);
          if (action === 'resolve') {
            return resolve();
          }

          if (action !== undefined) {
            return reject(action);
          }
        }

        createErrorHandler({
          description,
          dispatch,
          failedAction: failedFunc,
          ajaxCallId,
          showErrorPage,
          shouldIncrementErrorCount
        })(reason);
      });
  });
}

/**
 * Makes a basic HTTP request without Redux integration.
 * Automatically adds Content-Type header for requests with a body,
 * sets default method to GET, and includes product/answers cookies in headers.
 *
 * @template BodyModelT - The type of the request body.
 * @template ResponseModelT - The type of the expected response payload.
 * @param options - The request options (URL, method, headers, data, etc.).
 * @returns A promise that resolves with the response data.
 */
export function fetchResolverBasic<BodyModelT, ResponseModelT>(
  options: HpRequestOptions | HpRequestBodyOptions<BodyModelT>
): Promise<ResponseModelT> {
  options = { ...options };
  if ((options as HpRequestBodyOptions<BodyModelT>).data && (!options.headers || !options.headers['Content-Type'])) {
    options.headers = applyChanges(options.headers || {}, { 'Content-Type': 'application/json' });
  }

  options.method = options.method || 'GET';
  const wasLogged = logInfo(options as any, HpClientDebugFlags.AjaxRequestBodies | HpClientDebugFlags.AjaxRequests);
  const { method, url } = options;
  if (!wasLogged) {
    logInfo({ method, url }, HpClientDebugFlags.AjaxRequests);
  }

  options.headers = {
    [productKey]: getProductIdCookie(),
    [answersKey]: getAnswersIdCookie()
  };

  const result = callRestApi<ResponseModelT>(options);
  result.catch(voidFunction); // code quality check.
  return result.then(({ data }) => {
    return data;
  });
}

/**
 * Props for creating an error handler function.
 * Used internally to configure how errors are processed and displayed.
 */
export interface HpCreateErrorHandlerProps {
  /** The AJAX call ID for tracking the request lifecycle. */
  ajaxCallId?: string;
  /** Human-readable description of the failed request. */
  description: string;
  /** The Redux dispatch function. */
  dispatch: HpDispatchFunction<any>;
  /** Custom handler for failed requests. */
  failedAction?: HpFailedActionFunction;
  /** Whether to increment the error count. Defaults to true. */
  shouldIncrementErrorCount?: boolean;
  /** Whether to show the error page. Defaults to true. */
  showErrorPage?: boolean;
}

function createErrorHandler({
  description,
  dispatch,
  failedAction,
  ajaxCallId,
  showErrorPage = true,
  shouldIncrementErrorCount = true
}: HpCreateErrorHandlerProps): (reason: unknown) => void {
  return (reason: unknown): void => {
    const error = reason as HpWebRequestError<Record<string, any>>;
    let message: string | undefined;
    let responseData: Record<string, any> | undefined;
    let errorCode: string | undefined;
    if (error.isHpError) {
      ({ message, responseData, errorCode } = error);
    } else if (error && error.message) {
      ({ message } = error as Error);
    } else {
      try {
        message = JSON.stringify(reason);
      } catch (ex) {
        // This may provide some detail as to why it couldn't be serialized.
        message = 'An unexpected error type has been caught: ' + (ex as Error).message;
      }
    }

    postScriptError(reason as any, 'warn');

    let rsp: HpCustomFailureHandlerResponse | undefined;
    if (failedAction) {
      rsp = failedAction(reason) || rsp;
    }

    const { dispatchFetchError, showErrorPage: showErrPg, fetchError: errorPayload } = rsp || {};
    if (dispatchFetchError !== false) {
      if (showErrPg !== undefined) {
        showErrorPage = showErrPg;
      }

      let fetchError: string | HpFetchErrorPayload = '';
      if (showErrorPage) {
        if (errorPayload) {
          fetchError = errorPayload;
        } else {
          fetchError = "We're having problems connecting to our servers at the moment.";
        }
      }

      const action: HpFetchErrorOcurredAction = {
        type: 'FETCH_ERROR_OCCURRED',
        fetchError,
        description,
        message,
        errorDetails: responseData,
        ajaxCallId,
        showErrorPage,
        errorCode,
        shouldIncrementErrorCount
      };
      dispatch(action);
    } else {
      dispatch({ type: 'AJAX_CALL_COMPLETED', payload: { ajaxCallId } as any });
    }
  };
}

/**
 * Determines if an HTTP status code represents a successful response.
 * Considers 2xx and 4xx as success (4xx are client errors but valid responses),
 * while 3xx (redirects) and 5xx (server errors) are considered failures.
 *
 * @param statusCode - The HTTP status code to check.
 * @returns True if the status code indicates success, false otherwise.
 */
export function isSuccessStatus(statusCode: number): boolean {
  const isFailure = (statusCode >= 300 && statusCode < 400) || statusCode >= 500;
  return !isFailure;
}

/**
 * Example structure of failed request response data.
 * Note: There are no guarantees that all errors will match this structure,
 * though certain errors definitely do (e.g. when payment/setup fails during /save-details request).
 */
export interface HpFailedRequestData {
  /** The error code identifying the type of failure. */
  errorCode: string;
  /** The primary error message. */
  message: string;
  /** Array of detailed error messages. */
  messages: string[];
  /** The name/type of the error. */
  name: string;
  /** Whether the request was successful (always false for this type). */
  success: boolean;
  /** How long the request took before failing. */
  timeTaken: string;
}
